"use client";

import { Mic } from "lucide-react";
import { useEffect, useState } from "react";
import { DEFAULT_SPEECH_AUDIO_LEVELS } from "@/lib/prism/stt/mic-audio-pipeline";
import { SpeechLevelBars } from "@/modules/prism/ui/SpeechLevelBars";

type PrismSpeechMicButtonProps = {
    listening: boolean;
    audioLevels?: number[];
    disabled?: boolean;
    onToggle: () => void | Promise<void>;
};

export function PrismSpeechMicButton({
    listening,
    audioLevels = DEFAULT_SPEECH_AUDIO_LEVELS,
    disabled = false,
    onToggle,
}: PrismSpeechMicButtonProps) {
    const [reducedMotion, setReducedMotion] = useState(false);

    useEffect(() => {
        const media = window.matchMedia("(prefers-reduced-motion: reduce)");
        const update = () => setReducedMotion(media.matches);
        update();
        media.addEventListener("change", update);
        return () => media.removeEventListener("change", update);
    }, []);

    return (
        <button
            type="button"
            onClick={() => void onToggle()}
            disabled={disabled}
            aria-label={listening ? "Stop voice input" : "Start voice input"}
            aria-pressed={listening}
            title={listening ? "Stop dictation" : "Dictate with your voice"}
            className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors ${
                listening
                    ? "bg-accent-bg text-accent ring-2 ring-accent-line/70"
                    : "text-ink-2 hover:bg-surface-1 hover:text-ink-0"
            } disabled:opacity-50`}
        >
            {listening ? (
                <SpeechLevelBars levels={audioLevels} reducedMotion={reducedMotion} />
            ) : (
                <Mic className="w-5 h-5" />
            )}
        </button>
    );
}
